import { PiWarning, PiDownloadSimple } from 'react-icons/pi'
import { cn } from 'components/UI/cn'
import { ExistingContactMode } from '../wizardState'
import { radioGroupKeyDown } from './radioGroupKeyDown'

interface ModeOption {
  value: ExistingContactMode
  label: string
  description: string
}

const MODE_OPTIONS: ModeOption[] = [
  {
    value: 'keep',
    label: 'Keep existing',
    description:
      'Skip these rows — they won’t be imported and existing contacts are left unchanged.',
  },
  {
    value: 'update',
    label: 'Update with imported values',
    description:
      "Import these rows and update the existing contacts. Blank cells won't overwrite existing data.",
  },
]

interface ModeRadioGroupProps {
  selected: ExistingContactMode
  onSelect: (value: ExistingContactMode) => void
}

/**
 * CRM-duplicate handling mode selector. Uses the same DS2026 radio-card style as
 * the ImportOptionsModal's destination picker for consistency.
 */
function ModeRadioGroup({ selected, onSelect }: ModeRadioGroupProps) {
  return (
    <div
      role="radiogroup"
      aria-label="How to handle contacts that already exist in your CRM"
      className="grid grid-cols-2 gap-2"
    >
      {MODE_OPTIONS.map((opt) => {
        const checked = selected === opt.value
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={checked ? 0 : -1}
            onClick={() => onSelect(opt.value)}
            onKeyDown={(e) =>
              radioGroupKeyDown(e, MODE_OPTIONS, selected, onSelect)
            }
            className={cn(
              'flex items-start gap-3 rounded-xl border-2 px-4 py-3 text-left transition-colors',
              checked
                ? 'border-brand-active bg-basil-50'
                : 'border-line hover:border-line-form'
            )}
          >
            <span
              className={cn(
                'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
                checked
                  ? 'border-brand-active bg-brand-active'
                  : 'border-line-form bg-surface'
              )}
            >
              {checked && <span className="size-1.5 rounded-full bg-white" />}
            </span>
            <span className="flex flex-col">
              <span
                className={cn(
                  'text-sm font-semibold',
                  checked ? 'text-brand-active' : 'text-fg'
                )}
              >
                {opt.label}
              </span>
              <span className="mt-0.5 text-xs text-fg-secondary">
                {opt.description}
              </span>
            </span>
          </button>
        )
      })}
    </div>
  )
}

interface DecisionBarProps {
  /** Count of rows excluded because they have validation errors. */
  errorCount: number
  /** Count of rows whose email already exists in the org's CRM. */
  crmDuplicateCount: number
  existingContactMode: ExistingContactMode
  onExistingContactModeChange: (value: ExistingContactMode) => void
  willImport: number
  total: number
  onDownloadFailed: () => void
}

/**
 * "Review" bar shown above the wizard footer when the import needs a decision.
 *
 * Two independent sections, each only rendered when relevant:
 *
 * - Errors block (errorCount > 0): errored rows are ALWAYS excluded — there is
 *   no user choice. Shows the count plus a "Download N failed rows" link so the
 *   user can fix and re-upload.
 * - CRM-duplicate mode (crmDuplicateCount > 0): contacts whose email already
 *   exists in the CRM are handled per the chosen mode — "Keep existing"
 *   (default; the rows are EXCLUDED/skipped, existing contacts untouched) or
 *   "Update with imported values" (the rows are imported and the existing
 *   contacts updated, blank cells preserved). The willImport count reflects the
 *   mode. In-file duplicates are not shown here; the backend merges them
 *   automatically and they always import.
 *
 * When neither errors nor CRM duplicates exist, the caller hides this bar.
 */
export function DecisionBar({
  errorCount,
  crmDuplicateCount,
  existingContactMode,
  onExistingContactModeChange,
  willImport,
  total,
  onDownloadFailed,
}: DecisionBarProps) {
  return (
    <div className="flex flex-col gap-3 border-t border-line bg-surface px-8 pb-3 pt-4">
      {/* Errors — informational context; the download link sits inline with it. */}
      {errorCount > 0 && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="flex items-center gap-2 text-sm text-fg-secondary">
            <PiWarning className="size-4 shrink-0 text-warning-strong" />
            {errorCount} row{errorCount !== 1 ? 's' : ''} ha
            {errorCount !== 1 ? 've' : 's'} errors and won&rsquo;t be imported.
          </span>
          <button
            type="button"
            onClick={onDownloadFailed}
            className="flex items-center gap-1.5 text-sm font-medium text-brand-bright transition-colors hover:text-brand-active sm:ml-auto"
          >
            <PiDownloadSimple className="size-3.5" />
            Download {errorCount} failed row{errorCount !== 1 ? 's' : ''}
          </button>
        </div>
      )}

      {/* The decision — the only action in this bar, so it's the focal point.
          When the errors line is also showing, a divider separates the two. */}
      {crmDuplicateCount > 0 && (
        <div
          className={cn(
            'flex flex-col gap-2',
            errorCount > 0 && 'border-t border-line pt-3'
          )}
        >
          <span className="text-sm font-semibold text-fg">
            {crmDuplicateCount} contact
            {crmDuplicateCount !== 1 ? 's' : ''} already exist
            {crmDuplicateCount !== 1 ? '' : 's'} in your CRM — how should we
            handle {crmDuplicateCount !== 1 ? 'them' : 'it'}?
          </span>
          <ModeRadioGroup
            selected={existingContactMode}
            onSelect={onExistingContactModeChange}
          />
        </div>
      )}

      {/* Bottom-line outcome — de-emphasized. */}
      <span className="text-xs text-fg-secondary">
        <span className="font-semibold text-fg">{willImport}</span> of {total}{' '}
        row{total !== 1 ? 's' : ''} will be imported
      </span>
    </div>
  )
}
